import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TablePagination,
  Button,
  Box,
} from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import ConfirmDialog from "../ConfirmDialog";
import { ResponseLoginResponse } from "../../services/OpenAPI";
import { isNotGeneralUser } from "../../constants/roles";

// データ構造定義
export type ShippedItem = {
  id: string | number; // ID（非表示）
  parentId?: string; // 親の ID（子行のみ） （非表示）
  planCreatedDate?: string; // プラン作成日
  userId?: number;
  user?: string; // ユーザ
  shipmentConfirmId?: string;
  planName?: string; // プラン名
  groupNum?: number; // グループ番号
  productCount?: number; // 商品数
  shippedDate?: string; // 出荷日
  skuCount?: number; // SKU数
  singleCount?: number; // 単品
  multiCount?: number; // セット
  statusId?: number;
  rowColor?: "yellow" | "green" | "default";
  isChecked?: boolean;
};

type Header = {
  label: string;
  key: keyof ShippedItem;
  width?: number | string;
};

type Props = {
  data: ShippedItem[];
  totalListCount: number;
  checkedIds: Set<string>;
  onCheckedChange: (newChecked: Set<string>) => void;
  onParentRowClick?: (parent: ShippedItem, children: ShippedItem[]) => void;
  handleBackStatus: () => void;
  onPageChange?: (page: number, limit: number) => void;
  loginUser: ResponseLoginResponse | null;
};

// ソート順の型
type Order = "asc" | "desc";

// 文字サイズ
const TABLE_HEADER_FONT_SIZE = "0.65rem";
const TABLE_BODY_FONT_SIZE = "1rem";

// ヘッダー定義
const headers: Header[] = [
  { label: "プラン作成日", key: "planCreatedDate", width: 80 },
  { label: "ユーザー", key: "user", width: 75 },
  { label: "FBA納品番号", key: "shipmentConfirmId", width: 100 },
  { label: "プラン名", key: "planName", width: 120 },
  { label: "グループ", key: "groupNum", width: 20 },
  { label: "商品数", key: "productCount", width: 20 },
  { label: "出荷日", key: "shippedDate", width: 30 },
  { label: "SKU数", key: "skuCount", width: 20 },
  { label: "単品", key: "singleCount", width: 20 },
  { label: "セット", key: "multiCount", width: 20 },
];

const ShippedTable = ({
  data,
  totalListCount,
  checkedIds,
  onCheckedChange,
  onParentRowClick,
  handleBackStatus,
  onPageChange,
  loginUser,
}: Props) => {
  const [orderBy, setOrderBy] = useState<keyof ShippedItem | null>(
    "planCreatedDate"
  );
  const [order, setOrder] = useState<Order>("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [dialogOpen, setDialogOpen] = useState(false);

  // const sortedData = useMemo(() => {
  //   return [...data].sort((a, b) => {
  //     const aDate = new Date(a.planCreatedDate ?? "").getTime();
  //     const bDate = new Date(b.planCreatedDate ?? "").getTime();
  //     return bDate - aDate; // 降順（新しい順）
  //   });
  // }, [data]);
  const sortedData = useMemo(() => {
    const sorted = [...data];
    if (orderBy) {
      sorted.sort((a, b) => {
        const aVal = a[orderBy];
        const bVal = b[orderBy];

        // 日付だけ特別に処理
        if (orderBy === "planCreatedDate") {
          const aDate = new Date(aVal as string).getTime();
          const bDate = new Date(bVal as string).getTime();
          return order === "asc" ? aDate - bDate : bDate - aDate;
        }

        const aStr = String(aVal ?? "");
        const bStr = String(bVal ?? "");
        return order === "asc"
          ? aStr.localeCompare(bStr, "ja")
          : bStr.localeCompare(aStr, "ja");
      });
    }
    return sorted;
  }, [data, orderBy, order]);

  const paginatedData = useMemo(() => {
    return sortedData.slice(0, rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  // ヘッダーのソート
  const handleSort = (key: keyof ShippedItem) => {
    const isAsc = orderBy === key && order === "asc";
    setOrderBy(key);
    setOrder(isAsc ? "desc" : "asc");
    setPage(0);
  };

  const handleParentCheck = (parentId: string) => {
    const newSet = new Set(checkedIds);
    if (newSet.has(parentId)) {
      newSet.delete(parentId);
    } else {
      newSet.add(parentId);
    }
    onCheckedChange(newSet);
  };

  const handleSelectAll = () => {
    const allIds = data.flatMap((item) => [
      String(item.id),
      ...data.filter((c) => c.parentId === item.id).map((c) => String(c.id)),
    ]);
    const newSet =
      checkedIds.size === allIds.length
        ? new Set<string>()
        : new Set<string>(allIds);

    onCheckedChange(newSet);
  };

  const hasChecked = checkedIds.size > 0;

  return (
    <>
      <ConfirmDialog
        open={dialogOpen}
        title="確認"
        message="納品処理をキャンセルしてプラン作成に戻します。よろしいですか？"
        buttons={[
          {
            label: "いいえ",
            variant: "outlined",
            onClick: () => setDialogOpen(false),
          },
          {
            label: "はい",
            variant: "contained",
            onClick: () => {
              setDialogOpen(false);
              handleBackStatus();
            },
          },
        ]}
      />

      <TablePagination
        component="div"
        labelRowsPerPage="1ページの表示件数："
        rowsPerPageOptions={[5, 10, 25, 50]}
        count={sortedData.length} // 全行数
        // count={totalListCount} // 全行数
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => {
          setPage(newPage);
          onPageChange?.(newPage, rowsPerPage);
        }}
        onRowsPerPageChange={(e) => {
          const newRowsPerPage = parseInt(e.target.value, 10);
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0); // 行数変更時は 1 ページ目へ
          onPageChange?.(0, newRowsPerPage);
        }}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}〜${to} 件／全 ${count} 件`
        }
      />
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    checkedIds.size > 0 && checkedIds.size === data.length
                  }
                  indeterminate={
                    checkedIds.size > 0 && checkedIds.size < data.length
                  }
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectAll();
                  }}
                />
              </TableCell>
              {headers.map((header) => (
                <TableCell
                  key={String(header.key)}
                  sortDirection={orderBy === header.key ? order : false}
                  sx={{
                    fontSize: TABLE_HEADER_FONT_SIZE,
                    px: 0,
                    width: header.width,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === header.key}
                    direction={orderBy === header.key ? order : "asc"}
                    onClick={() => handleSort(header.key)}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item) => (
              <TableRow
                key={item.id}
                onClick={() => {
                  if (onParentRowClick) {
                    const children = data.filter((c) => c.parentId === item.id);
                    onParentRowClick(item, children);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  backgroundColor:
                    item.rowColor === "yellow"
                      ? "#fff59d"
                      : item.rowColor === "green"
                      ? "#81c784"
                      : "inherit",
                  fontSize: TABLE_BODY_FONT_SIZE,
                }}
              >
                <TableCell
                  padding="checkbox"
                  sx={{ fontSize: TABLE_BODY_FONT_SIZE }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={checkedIds.has(String(item.id))}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleParentCheck(String(item.id));
                    }}
                  />
                </TableCell>
                {headers.map((header) => {
                  const value = item[header.key];
                  const align = typeof value === "number" ? "right" : "left";

                  // JAN はクリック無効
                  const isDisabledClickColumn =
                    header.key === "shipmentConfirmId" ||
                    header.key === "planName";

                  return (
                    <TableCell
                      key={String(header.key)}
                      align={align}
                      sx={{
                        fontSize: TABLE_BODY_FONT_SIZE,
                        width: header.width,
                        ...(isDisabledClickColumn && { cursor: "default" }),
                      }}
                      onClick={
                        isDisabledClickColumn
                          ? (e) => e.stopPropagation()
                          : undefined
                      }
                    >
                      {value}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {hasChecked && isNotGeneralUser(loginUser?.permissionId) && (
          <Box
            position="fixed"
            bottom={16}
            left={0}
            width="100%"
            display="flex"
            justifyContent="center"
            gap={30}
            zIndex={1000}
          >
            <Button
              variant="contained"
              color="primary"
              sx={{ bgcolor: "#2b2b4b", color: "#fff", px: 4 }}
              onClick={() => setDialogOpen(true)}
            >
              ステータス戻し
            </Button>
          </Box>
        )}
      </TableContainer>
    </>
  );
};

export default ShippedTable;
